import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import Recaptcha from 'react-google-recaptcha';
import { z } from 'zod';
import { Puff } from 'react-loader-spinner';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';
import { LOCALE_REPLACEMENT_FOR_RECAPTCHA } from '@rsc/gatsby-localization-helpers';

import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@rsc/ui-components/shadcn/Alert';
import { Button } from '@rsc/ui-components/shadcn/Button';
import { Input } from '@rsc/ui-components/shadcn/Input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@rsc/ui-components/shadcn/Form';
import isCompanyOwnedDealerZip from '@rsc/utils/isCompanyOwnedDealerZip';
import SuccessMessage from './SuccessMessage';

const useFormSchema = () => {
  const { tt } = useUiComponentsInternationalContext();

  return useMemo(
    () =>
      z.object({
        // company name is required
        companyName: z.string().min(1, {
          message: tt(
            s =>
              s.blockBuilderReferralFormComponent
                .companyRequiredValidationMessage
          ),
        }),
        email: z.preprocess(
          val => (typeof val === 'string' ? val.trim() : val),
          z
            .string()
            .min(1, {
              message: tt(
                s =>
                  s.blockBuilderReferralFormComponent
                    .emailIsRequiredValidationMessage
              ),
            }) // Check for empty input first
            .email({
              message: tt(
                s =>
                  s.blockBuilderReferralFormComponent
                    .useValidEmailAddressValidationMessage
              ),
            }) // Then validate email format
        ),
        zipCode: z.string().regex(/^\d{5}(?:[-\s]\d{4})?$/, {
          message: tt(
            s =>
              s.blockBuilderReferralFormComponent
                .zipCodeFormatMismatchValidationMessage
          ),
        }),
        phoneNumber: z
          .string()
          .regex(/^\+?1?\s*(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}$/, {
            message: tt(
              s =>
                s.blockBuilderReferralFormComponent
                  .useValidPhoneNumberValidationMessage
            ),
          }),
      }),
    [tt]
  );
};

interface GetInternalLinkProps {
  reference?: {
    _type: string;
    store?: {
      slug: {
        current;
      };
      productType: string;
    };
    slug?: {
      current;
    };
  };
  slug?: string;
  currency?: string;
  anchor?: string;
}

type GetInternalLinkFunction = (props: GetInternalLinkProps) => string;

interface EnrollmentFormProps {
  getInternalLink: GetInternalLinkFunction;
}

export default function EnrollmentForm({
  getInternalLink,
}: Readonly<EnrollmentFormProps>) {
  const recaptchaRef = useRef<Recaptcha>();
  const formTopRef = useRef<HTMLDivElement>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [generatedReferralCode, setGeneratedReferralCode] = useState<
    string | null
  >(null);
  const [formIsProcessing, setFormIsProcessing] = useState<boolean>(false);

  const { tt, locale } = useUiComponentsInternationalContext();
  const recaptchaLocale = LOCALE_REPLACEMENT_FOR_RECAPTCHA?.[locale] ?? locale;
  const formSchema = useFormSchema();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companyName: '',
      email: '',
      zipCode: '',
      phoneNumber: '',
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    // ✅ This will be type-safe and validated.
    recaptchaRef.current?.reset();
    setFormIsProcessing(true);
    setApiError(null);

    try {
      // Execute reCAPTCHA and get the token
      const gRecaptchaResponse = await recaptchaRef.current?.executeAsync();
      if (!gRecaptchaResponse) {
        setApiError(
          tt(
            s =>
              s.blockBuilderReferralFormComponent
                .platformCommunicationErrorMessage
          )
        );
        setFormIsProcessing(false);
        return;
      }

      // Perform the ZIP code eligibility check
      const isEligible = await isCompanyOwnedDealerZip(values.zipCode).catch(
        () => {
          // Handle any errors that occur during the eligibility check
          setApiError(
            tt(
              s =>
                s.blockBuilderReferralFormComponent
                  .platformCommunicationErrorMessage
            )
          );
          setFormIsProcessing(false);
          return false; // Ensure the operation doesn't proceed if there's an error
        }
      );

      if (!isEligible) {
        // pop alert if not eligible
        setApiError(
          tt(
            s =>
              s.blockBuilderReferralFormComponent
                .programUnavailableForRegionErrorMessage
          )
        );
        // Set an error on the zipCode field if it is not eligible
        form.setError('zipCode', {
          type: 'manual',
          message: tt(
            s =>
              s.blockBuilderReferralFormComponent
                .zipCodeNotEligibleValidationMessage
          ),
        });
        setFormIsProcessing(false);
        return; // Exit the function early if the ZIP code is not eligible
      }

      // Proceed with form submission logic
      const response = await fetch(process.env.GATSBY_REFERRAL_SIGNUP_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          companyName: values.companyName,
          email: values.email,
          phone: values.phoneNumber,
          postalCode: values.zipCode,
          program: 'invisible-fence-referrals',
          type: 'partner',
          recaptchaToken: gRecaptchaResponse,
        }),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const referralCodeData = await response.json();

      setGeneratedReferralCode(referralCodeData?.responseData?.referralCode);
      setFormIsProcessing(false);
    } catch (error) {
      console.error(error);
      setApiError(
        tt(
          s =>
            s.blockBuilderReferralFormComponent
              .platformCommunicationErrorMessage
        )
      );
      setFormIsProcessing(false);
    }
  }

  // Scroll to ErrorBox when an error occurs
  useEffect(() => {
    if (apiError && formTopRef.current) {
      const yOffset = -100; // Adjust this value as needed
      const y =
        formTopRef.current.getBoundingClientRect().top +
        window.scrollY +
        yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, [apiError]);

  return (
    <div className="lg:max-w-screen-sm" ref={formTopRef}>
      {generatedReferralCode && (
        <SuccessMessage
          referralCode={generatedReferralCode}
          getInternalLink={getInternalLink}
        />
      )}
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className={`space-y-6 ${
            formIsProcessing ? 'opacity-50 pointer-events-none ' : ''
          }
          ${generatedReferralCode ? 'hidden' : ''}            
          `}
        >
          {apiError && (
            <Alert variant="destructive">
              <AlertTitle>
                {tt(s => s.blockBuilderReferralFormComponent.errorMessageTitle)}
              </AlertTitle>
              <AlertDescription>{apiError}</AlertDescription>
            </Alert>
          )}
          <p>
            {tt(
              s =>
                s.blockBuilderReferralFormComponent
                  .unlessSpecifiedAllFieldsRequiredHint
            )}
          </p>

          <FormField
            control={form.control}
            name="companyName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {tt(s => s.blockBuilderReferralFormComponent.companyLabel)}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    hasError={!!form.formState.errors.companyName}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {tt(s => s.blockBuilderReferralFormComponent.emailLabel)}
                </FormLabel>
                <FormControl>
                  <Input {...field} hasError={!!form.formState.errors.email} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="zipCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {tt(s => s.blockBuilderReferralFormComponent.zipCodeLabel)}
                </FormLabel>
                <FormDescription>
                  {tt(s => s.blockBuilderReferralFormComponent.zipCodeHint)}
                </FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    hasError={!!form.formState.errors.zipCode}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {tt(
                    s => s.blockBuilderReferralFormComponent.phoneNumberLabel
                  )}
                </FormLabel>
                <FormDescription>
                  {tt(
                    s =>
                      s.blockBuilderReferralFormComponent.phoneNumberFormatHint
                  )}
                </FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    hasError={!!form.formState.errors.phoneNumber}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex items-center">
            <Button className="mr-2" type="submit" disabled={formIsProcessing}>
              {tt(
                s => s.blockBuilderReferralFormComponent.enrollInProgramButton
              )}
            </Button>
            <Puff
              color="#888"
              height={40}
              width={40}
              visible={formIsProcessing}
            />
          </div>
          <div className="py-4 text-sm text-gray-500 mb-4">
            {tt(
              s => s.blockBuilderReferralFormComponent.copyrightFirstLineMessage
            )}
            <br />
            {tt(
              s =>
                s.blockBuilderReferralFormComponent.copyrightSecondLineMessage,
              {
                year: new Date().getFullYear().toString(),
              }
            )}
          </div>
          <Recaptcha
            ref={recaptchaRef}
            size="invisible"
            sitekey={process.env.GATSBY_RECAPTCHA_KEY_INVISIBLE}
            badge="inline"
            hl={recaptchaLocale}
          />
        </form>
      </Form>
    </div>
  );
}
